import React from 'react';
import { Avatar, View, IconMicLine, Tooltip, Text } from '@smarterservices/smarter-ui';
import './styles.css';
import TimeElapsedCounter from '@ui/shared/components/Common/TimeElapsedCounter';

type AvatarColor = 'ash' | 'default' | 'shamrock' | 'barney' | 'crimson' | 'fire' | 'licorice' | 'ai' | 'brand';

/**
 * User object for AvatarStack
 */
export interface AvatarStackUser {
    /** User's display name */
    name: string;
    /** Optional avatar image URL */
    src?: string;
    /** Optional avatar background color (overrides default color) */
    color?: AvatarColor;
    /** Whether the user is currently talking */
    isTalking?: boolean;
    /** Optional focus start time for tooltip */
    focusedSince?: Date | string | number;
    /** Whether the user is the primary proctor (e.g., actively checking in a session) */
    isPrimary?: boolean;
}

/**
 * Talking indicator position
 */
export type TalkingIndicatorPosition =
    | 'top-start'
    | 'top'
    | 'top-end'
    | 'bottom-start'
    | 'bottom'
    | 'bottom-end'
    | 'start'
    | 'end';

/**
 * AvatarStack Component Props
 */
export interface AvatarStackProps {
    /** Array of user objects to display */
    users?: AvatarStackUser[];
    /** Stack direction - 'horizontal' or 'vertical' */
    direction?: 'horizontal' | 'vertical';
    /** Overlap amount in pixels (negative for overlap, positive for spacing) */
    overlap?: number;
    /** Avatar size */
    size?: 'xx-small' | 'x-small' | 'small' | 'medium' | 'large' | 'x-large';
    /** Default avatar color (can be overridden per user) */
    color?: AvatarColor;
    /** Avatar shape */
    shape?: 'circle' | 'rectangle';
    /** Show tooltip on hover */
    showTooltip?: boolean;
    /** Custom tooltip renderer */
    renderTooltip?: (user: AvatarStackUser) => React.ReactNode;
    /** Position of the talking indicator */
    talkingIndicatorPosition?: TalkingIndicatorPosition;
    /** Whether to use inverse color for avatars */
    hasInverseColor?: boolean;
    /** Whether to show checkin indicator */
    showCheckinIndicator?: boolean;
    /** Color to use for primary users (defaults to 'fire') */
    primaryUserColor?: AvatarColor;
    /** Whether to use inverse color for primary users */
    primaryUserInverseColor?: boolean;
}

/**
 * AvatarStack Component
 * 
 * Displays a stack of avatars in horizontal or vertical orientation.
 * Supports overlapping, custom colors, and talking indicators.
 * 
 * @component
 * @example
 * // Horizontal stack with overlap
 * <AvatarStack 
 *   users={[
 *     { name: 'John Doe', src: '/avatar1.jpg', isTalking: true },
 *     { name: 'Jane Smith', color: 'success' },
 *     { name: 'Bob Johnson' }
 *   ]}
 *   direction="horizontal"
 *   overlap={5}
 *   size="xx-small"
 *   color="ash"
 * />
 * 
 * @example
 * // Vertical stack without overlap
 * <AvatarStack 
 *   users={users}
 *   direction="vertical"
 *   overlap={0}
 *   size="small"
 * />
 */
const AvatarStack: React.FC<AvatarStackProps> = ({
    users = [],
    direction = 'horizontal',
    overlap = 5,
    size = 'xx-small',
    color = 'ash',
    shape = 'circle',
    showTooltip = true,
    renderTooltip,
    talkingIndicatorPosition = 'bottom-start',
    hasInverseColor = false,
    primaryUserColor = 'fire',
    primaryUserInverseColor = false,
}) => {
    if (!users || users.length === 0) {
        return null;
    }

    /**
     * Default tooltip renderer
     */
    const defaultTooltipRenderer = (user: AvatarStackUser) => {
        if (user.focusedSince) {
            return (
                <View as="div" maxWidth="150px">
                    <Text variant="legend">
                        {user.name} has been focused for{' '}
                        <TimeElapsedCounter variant="legend" fromDate={user.focusedSince} />
                    </Text>
                </View>
            );
        }
        return user.name;
    };

    const tooltipRenderer = renderTooltip || defaultTooltipRenderer;

    /**
     * Get position styles for talking indicator based on position prop
     */
    const getTalkingIndicatorStyles = (): React.CSSProperties => {
        const baseStyles: React.CSSProperties = {
            position: 'absolute',
        };

        switch (talkingIndicatorPosition) {
            case 'top-start':
                return { ...baseStyles, top: '-4px', left: '-4px' };
            case 'top':
                return { ...baseStyles, top: '-6px', left: '50%', transform: 'translateX(-50%)' };
            case 'top-end':
                return { ...baseStyles, top: '-4px', right: '-4px' };
            case 'bottom-start':
                return { ...baseStyles, bottom: '-4px', left: '-4px' };
            case 'bottom':
                return { ...baseStyles, bottom: '-7px', left: '50%', transform: 'translateX(-50%)' };
            case 'bottom-end':
                return { ...baseStyles, bottom: '-4px', right: '-4px' };
            case 'start':
                return { ...baseStyles, top: '50%', left: '-7px', transform: 'translateY(-50%)' };
            case 'end':
                return { ...baseStyles, top: '50%', right: '-7px', transform: 'translateY(-50%)' };
            default:
                return { ...baseStyles, bottom: '-4px', left: '-4px' };
        }
    };

    const containerStyle: React.CSSProperties = {
        position: 'relative',
        display: direction === 'vertical' ? 'block' : 'inline-flex',
        flexDirection: direction === 'vertical' ? undefined : 'row',
        alignItems: undefined,
        textAlign: direction === 'vertical' ? 'center' : undefined,
    };

    return (
        <div style={containerStyle}>
            {users.map((user, index) => {
                // Calculate z-index based on direction
                // Horizontal: right items on top (higher index = higher z-index)
                // Vertical: top items on top (lower index = higher z-index)
                const baseZIndex = direction === 'horizontal'
                    ? index + 1
                    : users.length - index;

                const marginStyle: React.CSSProperties = {
                    position: 'relative',
                    zIndex: baseZIndex,
                    display: direction === 'vertical' ? 'block' : 'inline-block',
                };

                if (direction === 'horizontal' && index > 0) {
                    marginStyle.marginLeft = `-${overlap}px`;
                }

                if (direction === 'vertical' && index > 0) {
                    marginStyle.marginTop = `-${overlap}px`;
                }

                return (
                    <div
                        className="avatar-stack-user"
                        key={user.name || index}
                        style={marginStyle}
                    >
                        <div style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
                            {showTooltip ? (
                                <Tooltip
                                    color="primary"
                                    renderTip={() => tooltipRenderer(user)}
                                    placement="bottom"
                                >
                                    <span style={{ cursor: 'default' }}>
                                        <Avatar
                                            name={user.name}
                                            src={user.src}
                                            size={size}
                                            color={(user.isPrimary ? primaryUserColor : (user.color || color)) as any}
                                            shape={shape}
                                            hasInverseColor={user.isPrimary ? primaryUserInverseColor : hasInverseColor}
                                        />
                                    </span>
                                </Tooltip>
                            ) : (
                                <Avatar
                                    name={user.name}
                                    src={user.src}
                                    size={size}
                                    color={(user.isPrimary ? primaryUserColor : (user.color || color)) as any}
                                    shape={shape}
                                    hasInverseColor={user.isPrimary ? primaryUserInverseColor : hasInverseColor}
                                />
                            )}

                            {user.isTalking && (
                                <span
                                    className="avatar-stack-talking-indicator"
                                    style={{
                                        ...getTalkingIndicatorStyles(),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        backgroundColor: '#0B874B',
                                        border: '1px solid white',
                                        zIndex: 9999,
                                    }}
                                >
                                    <IconMicLine
                                        size="x-small"
                                        style={{
                                            fontSize: '8px',
                                            color: 'white',
                                        }}
                                    />
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default AvatarStack;
